import React from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogClose,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Button } from "../ui/button";

interface BrandDrugEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  values: {
    brand_drug_name: string;
    brand_drug_contents: string;
    brand_drug_formulation: string;
  };
  brandDrugId: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: (
    brandDrugId: string,
    values: {
      brand_drug_name: string;
      brand_drug_contents: string;
      brand_drug_formulation: string;
    }
  ) => void;
  onCancel: () => void;
  trigger: React.ReactNode;
}

const BrandDrugEditModal: React.FC<BrandDrugEditModalProps> = ({
  open,
  onOpenChange,
  values,
  brandDrugId,
  onChange,
  onSave,
  onCancel,
  trigger,
}) => {
  // Store original values in a ref so they persist during editing
  const originalValues = React.useRef({ ...values });

  // Update original values when modal opens or values change
  React.useEffect(() => {
    if (open) {
      originalValues.current = { ...values };
    }
  }, [open, values]);
  // Helper to check if a field is changed
  const isChanged = (field: keyof typeof values) =>
    values[field] !== originalValues.current[field];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="text-black">
        <DialogHeader>
          <DialogTitle>Edit Brand Drug</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-black">
              Name
            </label>
            <Input
              name="brand_drug_name"
              value={values.brand_drug_name}
              onChange={onChange}
              className={
                isChanged("brand_drug_name")
                  ? "border-red-500 ring-2 ring-red-200"
                  : ""
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">
              Contents
            </label>
            <Input
              name="brand_drug_contents"
              value={values.brand_drug_contents}
              onChange={onChange}
              className={
                isChanged("brand_drug_contents")
                  ? "border-red-500 ring-2 ring-red-200"
                  : ""
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">
              Formulation
            </label>
            <RadioGroup
              name="brand_drug_formulation"
              value={values.brand_drug_formulation}
              onValueChange={(val: string) =>
                onChange({
                  target: { name: "brand_drug_formulation", value: val },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              className={
                "flex flex-wrap gap-2 mt-2 " +
                (isChanged("brand_drug_formulation")
                  ? "ring-2 ring-red-200 border-red-500"
                  : "")
              }
            >
              <RadioGroupItem value="TABLET" label="Tablet" />
              <RadioGroupItem value="CAPSULE" label="Capsule" />
              <RadioGroupItem value="SYRUP" label="Syrup" />
              <RadioGroupItem value="SACHET" label="Sachet" />
              <RadioGroupItem value="SUPPOSITORY" label="Suppository" />
              <RadioGroupItem value="OINTMENT" label="Ointment" />
              <RadioGroupItem value="LINIMENT" label="Liniment" />
              <RadioGroupItem value="NASAL_SPRAY" label="Nasal Spray" />
            </RadioGroup>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button
            size="sm"
            onClick={() => onSave(brandDrugId, values)}
            variant="default"
          >
            Save
          </Button>
          <DialogClose asChild>
            <Button size="sm" onClick={onCancel} variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BrandDrugEditModal;
